import router, { generateAbsoluteLink } from '@/router';

export * from './types';

import axios from 'axios';
import type { AxiosInstance, AxiosResponse } from 'axios';
import localforage from 'localforage';
import type { IAuthData, IAuthNvData, IAuthResponse } from './types';

export class AuthService {
  dbName = 'NVIDIA_AEM';

  config: ImportMetaEnv = {};

  axios: AxiosInstance = axios.create();
  auth?: IAuthData;

  async init(config: ImportMetaEnv): Promise<boolean> {
    this.config = config;
    this.axios = axios.create({
      baseURL: this.config.NV_AUTH_GATEWAY
    });

    const usePrimeAuth = !this.config.NV_SSO_URL;

    if (!usePrimeAuth) {
      const nvClientId = new URL(this.config.NV_SSO_URL).searchParams.get('client_id');
      const nvAuthData = await window.NVIDIAGDC.getSession({
        redirectToLogin: true
        // starfleetConfigOverrides: { CLIENT_ID: nvClientId }
      });

      if (nvAuthData) {
        const nvAccessToken = nvAuthData.access_token;
        const nvIdToken = nvAuthData.id_token;

        await this.exchangeNvToken(nvAccessToken, nvIdToken)
          .then((auth) => {
            if (auth) {
              this.auth = auth;

              // localforage.setItem(this.dbName, auth);

              const isRegistrationPage = window.location.href.includes('/registration');
              if (!this.auth.profile_completed && !isRegistrationPage) {
                router.push('/registration');
              }
            }

            return !!auth;
          })
          .catch(() => false);
      }
    } else {
      const localAuth = await localforage.getItem<IAuthData>(this.dbName).catch(() => null);

      if (localAuth && localAuth.access_token) {
        await this.refreshToken(localAuth.refresh_token)
          .then((auth) => {
            this.auth = auth;
          })
          .catch(() => false);
      }

      if (!this.auth) {
        await this.authenticate()
          .then((auth) => {
            if (auth) {
              this.auth = auth;

              localforage.setItem(this.dbName, auth);
            }

            return !!auth;
          })
          .catch(() => false);
      }
    }

    return Promise.resolve(!!this.auth);
  }

  async authenticate(): Promise<IAuthData | undefined> {
    const queryData = new URLSearchParams(window.location.search);

    if (queryData.get('code')) {
      return this.axios
        .post(`?prime=true`, {
          code: queryData.get('code')
        })
        .then((response: AxiosResponse<IAuthResponse<IAuthData>>) => {
          return response.data?.isSuccess ? response.data?.data : undefined;
        })
        .catch((e) => {
          console.error(e);

          return undefined;
        });
    } else {
      return Promise.reject();
    }
  }

  async refreshToken(token: string): Promise<IAuthData | undefined> {
    return this.axios
      .post(`refresh`, {
        code: token
      })
      .then((response: AxiosResponse<IAuthResponse<IAuthData>>) =>
        response.data?.isSuccess ? response.data?.data : undefined
      )
      .catch(() => undefined);
  }

  async exchangeNvToken(nvAccessToken: string, nvIdToken: string) {
    return this.axios
      .post(
        ``,
        {
          code: nvAccessToken
        }
        // {
        //   headers: {
        //     Authorization: `Bearer ${nvIdToken}`
        //   }
        // }
      )
      .then((response: AxiosResponse<IAuthResponse<IAuthData>>) =>
        response.data?.isSuccess ? response.data?.data : undefined
      )
      .catch(() => undefined);
  }

  async logout() {
    await localforage.removeItem(this.dbName);

    if (this.auth) {
      window.open(
        `${this.config.NV_BASE_PRIME_API}logout?accountId=${this.auth.account_id}&` + `userId=${this.auth.user_id}`,
        '_self'
      );
    }
  }

  login() {
    let authLink =
      `${this.config.NV_BASE_PRIME_API}oauth/o/authorize?` +
      `client_id=${this.config.NV_CLIENT_ID}&` +
      `redirect_uri=${location.origin}${location.pathname}${location.search}&` +
      'scope=learner:read,learner:write&' +
      'response_type=CODE';

    if (this.config.NV_ACCOUNT_ID) {
      authLink += `&account=${this.config.NV_ACCOUNT_ID}`;
    }

    if (this.config.NV_SSO_URL) {
      window.open(this.config.NV_SSO_URL + generateAbsoluteLink('home'), '_self');
    } else {
      window.open(authLink, '_self');
    }
  }

  //

  getAuth(): IAuthData | undefined {
    return this.auth;
  }
}
